// Website Workspace — per-client view for builds, audits, maintenance.
//
// v1 (Sitting 1): client list, ClientWebsiteWorkspace shell with tabs,
//   initialization flow, Overview tab.
// v2 (Sitting 2): build checklist tab, pages tab, aesthetics, audit, maintenance.
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Plus, X, Search, ChevronRight, ArrowLeft, ExternalLink, Edit2,
} from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { format, differenceInDays } from 'date-fns'
import { isActiveProfile } from './constants'

const safeFmt = (d, fmt) => {
  if (!d) return ''
  try { return format(new Date(d), fmt) } catch { return '' }
}

// Mode metadata — single source of truth
export const WEBSITE_MODES = [
  { value: 'new_build',         label: '🎨 New Build',           description: 'Building a brand new site from scratch' },
  { value: 'audit_rebuild',     label: '🔍 Audit + Rebuild',     description: 'Auditing the existing site, then rebuilding' },
  { value: 'audit_refresh',     label: '🛠️ Audit + Refresh',     description: 'Auditing, doing light modifications, then taking over maintenance' },
  { value: 'maintenance_only',  label: '🔧 Maintenance Only',    description: 'Site exists (we built it or inherited it); ongoing upkeep only' },
  { value: 'ad_landing_pages',  label: '🎯 Ad Landing Pages',    description: 'Building landing pages for ads — on a subdomain or their existing site' },
]
const getMode = v => WEBSITE_MODES.find(m => m.value === v)

// Where the ad landing pages live (only relevant for the ad_landing_pages mode)
export const LANDING_PAGE_LOCATIONS = [
  { value: 'subdomain',     label: 'Subdomain',     description: 'e.g. go.theirsite.com or offers.theirsite.com' },
  { value: 'existing_site', label: 'Existing site', description: 'New pages added to their current website' },
]

// ════════════════════════════════════════════════════════════
// Top-level: client list → workspace
// ════════════════════════════════════════════════════════════
export default function WebsiteWorkspaceView({ clients, profiles, currentUser, profile, selectedWebsiteClientId, onClearWebsiteClientId, onOpenTask }) {
  const [selectedClient, setSelectedClient] = useState(null)

  useEffect(() => {
    if (selectedWebsiteClientId && clients.length > 0) {
      const c = clients.find(x => x.id === selectedWebsiteClientId)
      if (c) {
        setSelectedClient(c)
        onClearWebsiteClientId && onClearWebsiteClientId()
      }
    }
  }, [selectedWebsiteClientId, clients, onClearWebsiteClientId])

  if (selectedClient) {
    return <ClientWebsiteWorkspace
      client={selectedClient}
      profiles={profiles}
      currentUser={currentUser}
      onOpenTask={onOpenTask}
      onBack={() => setSelectedClient(null)}
    />
  }

  return <ClientListView clients={clients} onSelect={c => setSelectedClient(c)} />
}

// ────────────────────────────────────────────────────────────
// Client list — search + simple card list. Shows mode pill if set.
// ────────────────────────────────────────────────────────────
function ClientListView({ clients, onSelect }) {
  const [search, setSearch] = useState('')
  const filtered = clients
    .filter(c => !c.archived_at)
    .filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))

  return (
    <div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)', marginBottom: 14 }}>🌐 Website Workspace</div>

      <div style={{ position: 'relative', marginBottom: 16, maxWidth: 480 }}>
        <Search size={14} style={{ position: 'absolute', top: 11, left: 12, color: 'var(--text-muted)' }} />
        <input
          className="search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search clients..."
          style={{ paddingLeft: 34 }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.length === 0 && (
          <div className="empty-state"><h3>No clients</h3><p>{search ? 'No clients match your search.' : 'Add a client to get started.'}</p></div>
        )}
        {filtered.map(c => {
          const mode = getMode(c.website_mode)
          return (
            <div key={c.id} className="card" style={{ cursor: 'pointer' }} onClick={() => onSelect(c)}>
              <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {mode
                      ? <>{mode.label}</>
                      : <span style={{ fontStyle: 'italic' }}>No website workspace yet</span>}
                  </div>
                </div>
                <ChevronRight size={16} color="var(--text-muted)" />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// Per-client workspace
// ════════════════════════════════════════════════════════════
function ClientWebsiteWorkspace({ client, profiles, currentUser, onBack, onOpenTask }) {
  const toast = useToast()
  const [activeTab, setActiveTab] = useState('overview')
  const [initialLoading, setInitialLoading] = useState(true)
  const [siteData, setSiteData] = useState(null)
  const [buildTask, setBuildTask] = useState(null)
  const [maintenanceTask, setMaintenanceTask] = useState(null)
  const [pages, setPages] = useState([])
  const [showInitPicker, setShowInitPicker] = useState(false)

  // Track mode locally so we can update it without reloading the whole client
  const [websiteMode, setWebsiteMode] = useState(client.website_mode)

  const load = useCallback(async () => {
    const [{ data: site }, { data: builds, error: buildsErr }, { data: maint }, { data: pgs }] = await Promise.all([
      supabase.from('website_site_data').select('*').eq('client_id', client.id).maybeSingle(),
      // Use limit(1) + order so we get the most recent task if (somehow) duplicates exist
      supabase.from('tasks').select('*').eq('client_id', client.id).eq('template_id', 'std-website-build-checklist').order('created_at', { ascending: false }).limit(1),
      supabase.from('tasks').select('*').eq('client_id', client.id).eq('template_id', 'std-website-monthly-maintenance').order('date_due', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('seo_pages').select('*').eq('client_id', client.id).order('sort_order').order('page_name'),
    ])
    if (buildsErr) console.error('Build task load error:', buildsErr)
    setSiteData(site || null)
    setBuildTask((builds && builds[0]) || null)
    setMaintenanceTask(maint || null)
    setPages(pgs || [])
    setInitialLoading(false)
  }, [client.id])

  useEffect(() => { load() }, [load])

  // If client has no website_mode set yet, show the init picker as the only content
  const isInitialized = !!websiteMode

  // Tab visibility per mode (per spec)
  const TABS = useMemo(() => {
    const all = [
      { id: 'overview', label: 'Overview', show: () => true },
      { id: 'build_checklist', label: 'Build Checklist', show: m => ['new_build', 'audit_rebuild', 'audit_refresh'].includes(m) },
      { id: 'audit', label: 'Audit', show: m => ['audit_rebuild', 'audit_refresh'].includes(m) },
      { id: 'aesthetics', label: 'Aesthetics', show: m => ['new_build', 'audit_rebuild'].includes(m) },
      { id: 'pages', label: `Pages (${pages.length})`, show: () => true },
      { id: 'maintenance', label: 'Maintenance', show: () => true },
      { id: 'reference', label: 'Reference', show: () => true },
    ]
    return all.filter(t => t.show(websiteMode))
  }, [websiteMode, pages.length])

  // Update mode locally + persist
  const changeMode = async (newMode) => {
    const { error } = await supabase.from('clients').update({ website_mode: newMode }).eq('id', client.id)
    if (error) {
      toast('Could not save mode: ' + error.message, 'error')
      console.error('clients.update error:', error)
      return
    }
    setWebsiteMode(newMode)
    toast(`Mode set to ${getMode(newMode)?.label || newMode}`, 'success')
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={13} /> Back to clients</button>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>
          {client.name} · Website
        </div>
      </div>

      {initialLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
      ) : !isInitialized ? (
        <InitPicker client={client} profiles={profiles} currentUser={currentUser} onInitialized={(mode) => { setWebsiteMode(mode); load() }} />
      ) : (
        <>
          {/* Tabs */}
          <div className="tabs" style={{ marginBottom: 16 }}>
            {TABS.map(t => (
              <button key={t.id}
                className={`tab ${activeTab === t.id ? 'active' : ''}`}
                onClick={() => setActiveTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <OverviewTab
              client={client}
              websiteMode={websiteMode}
              changeMode={changeMode}
              siteData={siteData}
              onSiteDataUpdated={load}
              buildTask={buildTask}
              maintenanceTask={maintenanceTask}
              pages={pages}
              profiles={profiles}
              onOpenTask={onOpenTask}
            />
          )}

          {activeTab === 'build_checklist' && (
            <BuildChecklistTab buildTask={buildTask} profiles={profiles} onOpenTask={onOpenTask} />
          )}

          {activeTab === 'pages' && (
            <PagesTab
              client={client}
              pages={pages}
              profiles={profiles}
              hasSeo={true}   // Always show SEO fields (no services gating yet)
              onReload={load}
              currentUser={currentUser}
            />
          )}

          {activeTab === 'aesthetics' && (
            <AestheticsTab client={client} profiles={profiles} onOpenTask={onOpenTask} />
          )}

          {activeTab === 'audit' && (
            <AuditTab client={client} profiles={profiles} onOpenTask={onOpenTask} />
          )}

          {activeTab === 'maintenance' && (
            <MaintenanceTab client={client} maintenanceTask={maintenanceTask} profiles={profiles} onOpenTask={onOpenTask} onReload={load} />
          )}

          {activeTab === 'reference' && (
            <ReferenceTab />
          )}
        </>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Initialization picker — pick a mode, optionally assign someone
// ────────────────────────────────────────────────────────────
function InitPicker({ client, profiles, currentUser, onInitialized }) {
  const toast = useToast()
  const [mode, setMode] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [lpLocation, setLpLocation] = useState('') // for ad_landing_pages mode
  const [saving, setSaving] = useState(false)

  const launch = async () => {
    if (!mode) { toast('Pick a mode', 'error'); return }
    if (mode === 'ad_landing_pages' && !lpLocation) { toast('Pick where the landing pages will live', 'error'); return }
    setSaving(true)
    try {
      // 1. Set the client's website_mode
      const { error: clientUpdateErr } = await supabase.from('clients').update({ website_mode: mode }).eq('id', client.id)
      if (clientUpdateErr) {
        toast('Could not save mode to client: ' + clientUpdateErr.message, 'error')
        console.error('clients.update error:', clientUpdateErr)
        setSaving(false)
        return
      }

      // 2. Create the corresponding starter task depending on the mode
      const needsBuildChecklist = ['new_build', 'audit_rebuild', 'audit_refresh'].includes(mode)
      if (needsBuildChecklist) {
        // Load the template to copy its checklist_items
        const { data: tpl } = await supabase.from('task_templates').select('*').eq('id', 'std-website-build-checklist').maybeSingle()
        if (tpl) {
          await supabase.from('tasks').insert({
            title: tpl.title,
            description: tpl.description,
            client_id: client.id,
            template_id: tpl.id,
            assigned_to: assignedTo || null,
            assigned_by: currentUser.id,
            status: 'not_started',
            service_area: 'website',
            approval_path: 'internal',
            checklist_items: (tpl.checklist_items || []).map(it => ({ ...it, checked: false })),
            triggered_by: { source: 'website_workspace_init', mode },
          })
        }
      } else if (mode === 'maintenance_only') {
        // Spawn the first monthly maintenance task immediately
        const { data: tpl } = await supabase.from('task_templates').select('*').eq('id', 'std-website-monthly-maintenance').maybeSingle()
        if (tpl) {
          const due = new Date()
          due.setMonth(due.getMonth() + 1)
          due.setDate(1)
          await supabase.from('tasks').insert({
            title: tpl.title,
            description: tpl.description,
            client_id: client.id,
            template_id: tpl.id,
            assigned_to: assignedTo || null,
            assigned_by: currentUser.id,
            status: 'not_started',
            service_area: 'website',
            approval_path: 'none',
            date_due: due.toISOString().slice(0, 10),
            checklist_items: (tpl.checklist_items || []).map(it => ({ ...it, checked: false })),
            is_recurring: true,
            recurrence_type: 'monthly',
            recurrence_day_of_month: 1,
            triggered_by: { source: 'website_workspace_init', mode },
          })
        }
      }

      // 3. Initialize the website_site_data row (store LP location if applicable)
      await supabase.from('website_site_data').upsert({
        client_id: client.id,
        ...(mode === 'ad_landing_pages' ? { landing_page_location: lpLocation } : {}),
      })

      toast('Website workspace launched!', 'success')
      onInitialized(mode)
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" style={{ maxWidth: 560 }}>
      <div className="card-header">
        <span className="card-title">🌐 Launch Website Workspace</span>
      </div>
      <div className="card-body">
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          Pick the mode that fits this client's situation. You can change it later.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {WEBSITE_MODES.map(m => (
            <label key={m.value} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
              background: mode === m.value ? 'var(--cream)' : '#fff',
              border: `1.5px solid ${mode === m.value ? 'var(--gold)' : 'var(--border-light)'}`,
              borderRadius: 8, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="website_mode"
                value={m.value}
                checked={mode === m.value}
                onChange={() => setMode(m.value)}
                style={{ marginTop: 3 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{m.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{m.description}</div>
              </div>
            </label>
          ))}
        </div>

        {/* Landing page location — only for ad_landing_pages mode */}
        {mode === 'ad_landing_pages' && (
          <div className="form-group">
            <label className="form-label">Where will the landing pages live?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {LANDING_PAGE_LOCATIONS.map(loc => (
                <label key={loc.value} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px',
                  background: lpLocation === loc.value ? 'var(--cream)' : '#fff',
                  border: `1.5px solid ${lpLocation === loc.value ? 'var(--gold)' : 'var(--border-light)'}`,
                  borderRadius: 8, cursor: 'pointer',
                }}>
                  <input type="radio" name="lp_location" value={loc.value}
                    checked={lpLocation === loc.value} onChange={() => setLpLocation(loc.value)} style={{ marginTop: 3 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{loc.label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{loc.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Assign starter task to (optional)</label>
          <select className="form-control" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
            <option value="">Leave unassigned</option>
            {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            For build modes, this creates the Website Build Checklist task. For maintenance mode, this schedules the first monthly maintenance task.
          </div>
        </div>

        <button className="btn btn-gold" onClick={launch} disabled={saving || !mode} style={{ marginTop: 8 }}>
          {saving ? 'Launching...' : '🚀 Launch Website Workspace'}
        </button>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Overview tab
// ────────────────────────────────────────────────────────────
function OverviewTab({ client, websiteMode, changeMode, siteData, onSiteDataUpdated, buildTask, maintenanceTask, pages, profiles, onOpenTask }) {
  const mode = getMode(websiteMode)
  const [showModeChange, setShowModeChange] = useState(false)

  // Build progress (from checklist completion)
  const buildItems = buildTask?.checklist_items || []
  const buildDone = buildItems.filter(i => i.checked).length
  const buildPct = buildItems.length ? Math.round((buildDone / buildItems.length) * 100) : 0

  // Pages live count
  const pagesLive = pages.filter(p => p.build_status === 'live').length

  // Going-live readiness — derived from the last 4 items of the build checklist (AD present, domain, host, take site live)
  const goLiveItems = buildItems.slice(-4)
  const goLiveReady = goLiveItems.length === 4 && goLiveItems.slice(0, 3).every(i => i.checked)

  return (
    <div>
      {/* Mode pill row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ background: 'var(--navy)', color: '#fff', padding: '6px 14px', borderRadius: 50, fontSize: 13, fontWeight: 600 }}>
          {mode?.label || websiteMode}
        </span>
        {websiteMode === 'ad_landing_pages' && siteData?.landing_page_location && (
          <span style={{ background: 'var(--cream-dark)', color: 'var(--navy)', padding: '6px 14px', borderRadius: 50, fontSize: 12.5, fontWeight: 600 }}>
            📍 {LANDING_PAGE_LOCATIONS.find(l => l.value === siteData.landing_page_location)?.label || siteData.landing_page_location}
          </span>
        )}
        <button className="btn btn-ghost btn-sm" onClick={() => setShowModeChange(s => !s)}>
          {showModeChange ? 'Cancel' : 'Change mode'}
        </button>
      </div>

      {showModeChange && (
        <div className="card" style={{ marginBottom: 16, background: 'var(--cream)' }}>
          <div className="card-body" style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>Change mode:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {WEBSITE_MODES.filter(m => m.value !== websiteMode).map(m => (
                <button key={m.value} className="btn btn-ghost btn-sm"
                  onClick={async () => { await changeMode(m.value); setShowModeChange(false) }}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Build Checklist card — clickable, opens task in TaskDetail */}
      {['new_build', 'audit_rebuild', 'audit_refresh'].includes(websiteMode) && buildTask && (
        <div className="card" style={{
          marginBottom: 16,
          cursor: onOpenTask ? 'pointer' : 'default',
          borderLeft: `4px solid ${buildPct === 100 ? '#2e6e3a' : buildPct === 0 ? 'var(--text-muted)' : 'var(--gold)'}`,
        }} onClick={() => onOpenTask && onOpenTask(buildTask)}>
          <div className="card-body" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                {buildPct === 100 ? '✅' : buildPct === 0 ? '🔲' : '⚙️'} Website Build Checklist
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>
                  {buildDone} of {buildItems.length} steps · {pagesLive} of {pages.length} pages live
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--cream-dark)', borderRadius: 3, overflow: 'hidden', maxWidth: 320 }}>
                <div style={{
                  height: '100%',
                  width: `${buildPct}%`,
                  background: buildPct === 100 ? '#2e6e3a' : 'var(--gold)',
                  transition: 'width 200ms',
                }} />
              </div>
            </div>
            {onOpenTask && (
              <span style={{ fontSize: 11.5, color: 'var(--teal)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Open task →
              </span>
            )}
          </div>
        </div>
      )}

      {/* Going Live readiness card — visible in build modes */}
      {['new_build', 'audit_rebuild'].includes(websiteMode) && buildTask && goLiveItems.length === 4 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: `4px solid ${goLiveReady ? '#2e6e3a' : 'var(--gold)'}` }}>
          <div className="card-body" style={{ padding: '14px 18px' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
              {goLiveReady ? '✅ Ready to go live' : '🚦 Going Live Readiness'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              {goLiveItems.slice(0, 3).map((it, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, color: it.checked ? '#2e6e3a' : 'var(--text-muted)' }}>
                  <span>{it.checked ? '✓' : '○'}</span>
                  <span>{it.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Site reference card */}
      <SiteDataCard client={client} siteData={siteData} onUpdated={onSiteDataUpdated} />

      {/* Maintenance summary — only if mode is post-live or maintenance */}
      {(websiteMode === 'maintenance_only' || (websiteMode === 'audit_refresh' && maintenanceTask)) && (
        <MaintenanceSummaryCard maintenanceTask={maintenanceTask} profiles={profiles} onOpenTask={onOpenTask} />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Site Reference Card — editable per-client website data
// ────────────────────────────────────────────────────────────
function SiteDataCard({ client, siteData, onUpdated }) {
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    current_url: siteData?.current_url || '',
    wp_engine_signed_up: siteData?.wp_engine_signed_up || false,
    wp_engine_shared_with_team: siteData?.wp_engine_shared_with_team || false,
    divi_login_username: siteData?.divi_login_username || '',
    divi_login_password_link: siteData?.divi_login_password_link || '',
    lmr_site_login_link: siteData?.lmr_site_login_link || '',
    ga4_dashboard_url: siteData?.ga4_dashboard_url || '',
    final_color_palette: siteData?.final_color_palette || [],
    notes: siteData?.notes || '',
  })
  const [paletteInput, setPaletteInput] = useState('')

  useEffect(() => {
    setForm({
      current_url: siteData?.current_url || '',
      wp_engine_signed_up: siteData?.wp_engine_signed_up || false,
      wp_engine_shared_with_team: siteData?.wp_engine_shared_with_team || false,
      divi_login_username: siteData?.divi_login_username || '',
      divi_login_password_link: siteData?.divi_login_password_link || '',
      lmr_site_login_link: siteData?.lmr_site_login_link || '',
      ga4_dashboard_url: siteData?.ga4_dashboard_url || '',
      final_color_palette: siteData?.final_color_palette || [],
      notes: siteData?.notes || '',
    })
  }, [siteData])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const addColor = () => {
    const hex = paletteInput.trim()
    if (!/^#?[0-9a-fA-F]{6}$/.test(hex)) { toast('Enter a 6-digit hex (e.g. #B49C73)', 'error'); return }
    const normalized = hex.startsWith('#') ? hex : `#${hex}`
    set('final_color_palette', [...(form.final_color_palette || []), normalized])
    setPaletteInput('')
  }
  const removeColor = (i) => {
    set('final_color_palette', form.final_color_palette.filter((_, idx) => idx !== i))
  }

  const save = async () => {
    const { error } = await supabase.from('website_site_data').upsert({
      client_id: client.id,
      ...form,
      updated_at: new Date().toISOString(),
    })
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Site reference saved', 'success')
    setEditing(false)
    onUpdated()
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="card-title">🌐 Site Reference</span>
        {!editing ? (
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}><Edit2 size={12} /> Edit</button>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn btn-gold btn-sm" onClick={save}>Save</button>
          </div>
        )}
      </div>
      <div className="card-body">
        {!editing ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 14px', fontSize: 13 }}>
            <div style={{ color: 'var(--text-muted)' }}>Current URL</div>
            <div>{siteData?.current_url
              ? <a href={siteData.current_url} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>{siteData.current_url} <ExternalLink size={10} style={{ verticalAlign: 'middle' }} /></a>
              : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>not set</span>}
            </div>

            <div style={{ color: 'var(--text-muted)' }}>WP Engine</div>
            <div>
              {siteData?.wp_engine_signed_up ? '✓ Signed up' : '○ Not signed up'}
              {' · '}
              {siteData?.wp_engine_shared_with_team ? '✓ Shared with team' : '○ Not shared'}
            </div>

            <div style={{ color: 'var(--text-muted)' }}>Final color palette</div>
            <div>
              {siteData?.final_color_palette?.length
                ? <div style={{ display: 'flex', gap: 6 }}>
                    {siteData.final_color_palette.map((c, i) => (
                      <div key={i} title={c} style={{ width: 22, height: 22, borderRadius: 4, background: c, border: '1px solid var(--border)' }} />
                    ))}
                  </div>
                : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>not chosen yet</span>}
            </div>

            <div style={{ color: 'var(--text-muted)' }}>Divi login</div>
            <div>
              {siteData?.divi_login_username ? <span style={{ fontFamily: 'monospace' }}>{siteData.divi_login_username}</span> : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>not set</span>}
              {siteData?.divi_login_password_link && <> · <a href={siteData.divi_login_password_link} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)', fontSize: 12 }}>password doc ↗</a></>}
            </div>

            <div style={{ color: 'var(--text-muted)' }}>LMR site login</div>
            <div>
              {siteData?.lmr_site_login_link
                ? <a href={siteData.lmr_site_login_link} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>creds doc ↗</a>
                : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>not set</span>}
            </div>

            <div style={{ color: 'var(--text-muted)' }}>GA4 dashboard</div>
            <div>
              {siteData?.ga4_dashboard_url
                ? <a href={siteData.ga4_dashboard_url} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>open ↗</a>
                : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>not set</span>}
            </div>

            {siteData?.notes && (
              <>
                <div style={{ color: 'var(--text-muted)' }}>Notes</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{siteData.notes}</div>
              </>
            )}
          </div>
        ) : (
          <div>
            <div className="form-group">
              <label className="form-label">Current URL</label>
              <input className="form-control" value={form.current_url} onChange={e => set('current_url', e.target.value)} placeholder="https://..." />
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.wp_engine_signed_up} onChange={e => set('wp_engine_signed_up', e.target.checked)} />
                WP Engine signed up
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.wp_engine_shared_with_team} onChange={e => set('wp_engine_shared_with_team', e.target.checked)} />
                Shared with team
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">Final color palette</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                {(form.final_color_palette || []).map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid var(--border-light)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ width: 16, height: 16, borderRadius: 3, background: c, border: '1px solid var(--border)' }} />
                    <span style={{ fontFamily: 'monospace' }}>{c}</span>
                    <button onClick={() => removeColor(i)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, fontSize: 11 }}>×</button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={paletteInput} onChange={e => setPaletteInput(e.target.value)} placeholder="#B49C73" className="form-control" style={{ maxWidth: 140 }} onKeyDown={e => e.key === 'Enter' && addColor()} />
                <button className="btn btn-ghost btn-sm" onClick={addColor}>Add color</button>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Divi login username</label>
                <input className="form-control" value={form.divi_login_username} onChange={e => set('divi_login_username', e.target.value)} placeholder="laur1912" />
              </div>
              <div className="form-group">
                <label className="form-label">Password doc link</label>
                <input className="form-control" value={form.divi_login_password_link} onChange={e => set('divi_login_password_link', e.target.value)} placeholder="https://..." />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">LMR site credentials link</label>
              <input className="form-control" value={form.lmr_site_login_link} onChange={e => set('lmr_site_login_link', e.target.value)} placeholder="Google Doc URL where the LMR login for this client's site is saved" />
            </div>

            <div className="form-group">
              <label className="form-label">GA4 dashboard URL</label>
              <input className="form-control" value={form.ga4_dashboard_url} onChange={e => set('ga4_dashboard_url', e.target.value)} placeholder="https://analytics.google.com/..." />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Maintenance summary card
// ────────────────────────────────────────────────────────────
function MaintenanceSummaryCard({ maintenanceTask, profiles, onOpenTask }) {
  if (!maintenanceTask) {
    return (
      <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--text-muted)' }}>
        <div className="card-body" style={{ padding: '14px 18px', fontSize: 13, color: 'var(--text-muted)' }}>
          🔧 No monthly maintenance task scheduled yet. It will be auto-spawned when the build goes live.
        </div>
      </div>
    )
  }

  const items = maintenanceTask.checklist_items || []
  const done = items.filter(i => i.checked).length
  const isComplete = items.length > 0 && done === items.length
  const isOverdue = maintenanceTask.date_due && new Date(maintenanceTask.date_due) < new Date() && !isComplete
  const assignee = profiles?.find(p => p.id === maintenanceTask.assigned_to)

  const borderColor = isComplete ? '#2e6e3a' : isOverdue ? 'var(--red)' : 'var(--gold)'

  return (
    <div className="card" style={{ marginBottom: 16, borderLeft: `4px solid ${borderColor}`, cursor: onOpenTask ? 'pointer' : 'default' }}
      onClick={() => onOpenTask && onOpenTask(maintenanceTask)}>
      <div className="card-body" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
            🔧 Latest Monthly Maintenance · {done} / {items.length} steps · {isComplete ? '✓ Complete' : isOverdue ? '⚠️ Overdue' : '▶ In progress'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {assignee ? `Assigned to ${assignee.full_name}` : 'Unassigned'}
            {maintenanceTask.date_due && ` · Due ${safeFmt(maintenanceTask.date_due, 'MMM d, yyyy')}`}
          </div>
        </div>
        {onOpenTask && (
          <span style={{ fontSize: 11.5, color: 'var(--teal)', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Open task →
          </span>
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// SITTING 2: Build Checklist tab
// ════════════════════════════════════════════════════════════
function BuildChecklistTab({ buildTask, profiles, onOpenTask }) {
  if (!buildTask) {
    return (
      <div className="empty-state">
        <h3>No build checklist task</h3>
        <p>The Website Build Checklist task hasn't been created for this client. Try re-initializing the workspace from Overview, or have an admin create one.</p>
      </div>
    )
  }
  const items = buildTask.checklist_items || []
  const done = items.filter(i => i.checked).length
  const total = items.length
  const assignee = profiles?.find(p => p.id === buildTask.assigned_to)

  return (
    <div>
      <div className="card" style={{
        marginBottom: 16,
        cursor: onOpenTask ? 'pointer' : 'default',
        borderLeft: `4px solid ${done === total ? '#2e6e3a' : done === 0 ? 'var(--text-muted)' : 'var(--gold)'}`,
      }} onClick={() => onOpenTask && onOpenTask(buildTask)}>
        <div className="card-body" style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>📋 Website Build Checklist</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{done} of {total} steps done</div>
          </div>
          <div style={{ height: 6, background: 'var(--cream-dark)', borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
            <div style={{
              height: '100%',
              width: `${total ? (done / total * 100) : 0}%`,
              background: done === total ? '#2e6e3a' : 'var(--gold)',
              transition: 'width 200ms',
            }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {assignee ? `Assigned to ${assignee.full_name}` : 'Unassigned'}
            {buildTask.date_due && ` · Due ${safeFmt(buildTask.date_due, 'MMM d, yyyy')}`}
          </div>
          {onOpenTask && (
            <div style={{ fontSize: 11.5, color: 'var(--teal)', fontWeight: 600, marginTop: 8 }}>
              Click to open task and check off steps →
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <strong>About this checklist:</strong> 60 steps grouped into sections (Site Prep, Settings, Divi Theme Options, Site Identity, Typography, Header, Footer, Home Page, Finalize, Blog, Backups, Going Live). The Divi API key is embedded in step 7 — copy it from there when activating the theme. Going Live items require a supervising AD present + domain access + host access.
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// SITTING 2: Pages tab
// ════════════════════════════════════════════════════════════
const BUILD_STATUSES = [
  { value: 'not_started',  label: 'Not started',  color: 'var(--text-muted)' },
  { value: 'in_progress',  label: 'In progress',  color: 'var(--gold)' },
  { value: 'draft_ready',  label: 'Draft ready',  color: '#2a6e84' },
  { value: 'approved',     label: 'Approved',     color: 'var(--mauve)' },
  { value: 'live',         label: 'Live',         color: '#2e6e3a' },
]
const getBuildStatus = v => BUILD_STATUSES.find(s => s.value === v) || BUILD_STATUSES[0]

function PagesTab({ client, pages, profiles, hasSeo, onReload, currentUser }) {
  const toast = useToast()
  const [showAdd, setShowAdd] = useState(false)
  const [showBulkAdd, setShowBulkAdd] = useState(false)
  const [editingPage, setEditingPage] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [filter, setFilter] = useState('all')

  const filtered = pages.filter(p => {
    if (filter === 'key') return p.is_key_page
    if (filter === 'live') return p.build_status === 'live'
    if (filter === 'pending') return p.build_status !== 'live'
    return true
  })

  const setPageField = async (page, field, value) => {
    const { error } = await supabase.from('seo_pages').update({ [field]: value }).eq('id', page.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    onReload()
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: 'all', label: `All (${pages.length})` },
            { id: 'key', label: `⭐ Key (${pages.filter(p => p.is_key_page).length})` },
            { id: 'pending', label: `Pending (${pages.filter(p => p.build_status !== 'live').length})` },
            { id: 'live', label: `Live (${pages.filter(p => p.build_status === 'live').length})` },
          ].map(f => (
            <button key={f.id} className={`btn btn-sm ${filter === f.id ? 'btn-gold' : 'btn-ghost'}`}
              onClick={() => setFilter(f.id)}>{f.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowBulkAdd(true)}><Plus size={13} /> Bulk Add</button>
          <button className="btn btn-gold btn-sm" onClick={() => setShowAdd(true)}><Plus size={13} /> Add Page</button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div style={{ background: 'var(--navy)', color: '#fff', padding: '10px 16px', borderRadius: 10, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>{selectedIds.length} selected</span>
          <span style={{ opacity: 0.6 }}>·</span>
          <span>Bulk assign to:</span>
          <select onChange={async (e) => {
            if (!e.target.value) return
            const { error } = await supabase.from('seo_pages').update({ assigned_to: e.target.value }).in('id', selectedIds)
            if (error) { toast('Error: ' + error.message, 'error'); return }
            toast(`Assigned ${selectedIds.length} pages`, 'success')
            setSelectedIds([])
            onReload()
          }} value=""
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>
            <option value="">Pick a person...</option>
            {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id} style={{ color: '#000' }}>{p.full_name}</option>)}
          </select>
          <button onClick={async () => {
            if (!window.confirm(`Delete ${selectedIds.length} pages?`)) return
            await supabase.from('seo_pages').delete().in('id', selectedIds)
            toast(`Deleted ${selectedIds.length} pages`, 'success')
            setSelectedIds([])
            onReload()
          }} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
            Delete
          </button>
          <button onClick={() => setSelectedIds([])} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
            Clear
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
          <h3>{pages.length === 0 ? 'No pages yet' : 'No pages match this filter'}</h3>
          <p>{pages.length === 0
            ? 'Add pages as you plan them. Flag key pages (⭐) so the team knows the AD is building them as templates for the rest.'
            : 'Try a different filter.'}</p>
          {pages.length === 0 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
              <button className="btn btn-ghost" onClick={() => setShowBulkAdd(true)}><Plus size={13} /> Bulk Add</button>
              <button className="btn btn-gold" onClick={() => setShowAdd(true)}><Plus size={13} /> Add Page</button>
            </div>
          )}
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="task-table" style={{ width: '100%', minWidth: 800 }}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox"
                    checked={selectedIds.length === filtered.length && filtered.length > 0}
                    onChange={e => setSelectedIds(e.target.checked ? filtered.map(p => p.id) : [])} />
                </th>
                <th style={{ width: 32 }}>⭐</th>
                <th>Page</th>
                {hasSeo && <th>Keyword</th>}
                <th>Assignee</th>
                <th>Status</th>
                <th>Preview</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(p => {
                const status = getBuildStatus(p.build_status)
                const assignee = profiles.find(pr => pr.id === p.assigned_to)
                return (
                  <tr key={p.id} style={{ cursor: 'pointer', background: p.build_status === 'live' ? '#f6fbf7' : undefined }}
                    onClick={e => {
                      const tag = e.target.tagName
                      if (['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA', 'A'].includes(tag)) return
                      setEditingPage(p)
                    }}>
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.includes(p.id)}
                        onChange={e => setSelectedIds(ids => e.target.checked ? [...ids, p.id] : ids.filter(x => x !== p.id))} />
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button onClick={() => setPageField(p, 'is_key_page', !p.is_key_page)}
                        title={p.is_key_page ? 'Unflag as key page' : 'Flag as key page'}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0, opacity: p.is_key_page ? 1 : 0.25 }}>
                        ⭐
                      </button>
                    </td>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: p.build_status === 'live' ? 400 : 500 }}>
                        {p.page_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(untitled)</span>}
                      </div>
                      {hasSeo && (p.faq_questions || p.topics_to_include || p.structural_guidelines) && (
                        <div style={{ fontSize: 10.5, color: 'var(--teal)', marginTop: 2 }}>📝 Has SEO content guidance</div>
                      )}
                    </td>
                    {hasSeo && (
                      <td>
                        <input value={p.keyword_choice || ''} onChange={e => setPageField(p, 'keyword_choice', e.target.value)}
                          placeholder="—"
                          style={{ width: '100%', minWidth: 110, padding: '4px 6px', border: '1px solid transparent', borderRadius: 4, fontSize: 12.5, background: 'transparent' }}
                          onFocus={e => e.target.style.border = '1px solid var(--border)'}
                          onBlur={e => e.target.style.border = '1px solid transparent'} />
                      </td>
                    )}
                    <td>
                      <select value={p.assigned_to || ''} onChange={e => setPageField(p, 'assigned_to', e.target.value || null)}
                        style={{ fontSize: 11.5, padding: '3px 6px', border: '1px solid var(--border-light)', borderRadius: 6, maxWidth: 110 }}>
                        <option value="">Unassigned</option>
                        {profiles.filter(isActiveProfile).map(pr => <option key={pr.id} value={pr.id}>{pr.full_name}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={p.build_status || 'not_started'} onChange={e => setPageField(p, 'build_status', e.target.value)}
                        style={{ fontSize: 11.5, padding: '3px 6px', border: '1px solid var(--border-light)', borderRadius: 6, color: status.color, fontWeight: 600 }}>
                        {BUILD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td>
                      {p.preview_url
                        ? <a href={p.preview_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--teal)', fontSize: 12 }}>↗ Preview</a>
                        : p.final_url
                          ? <a href={p.final_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--teal)', fontSize: 12 }}>↗ Live</a>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-icon" onClick={async () => {
                        if (!window.confirm(`Delete page "${p.page_name}"?`)) return
                        await supabase.from('seo_pages').delete().eq('id', p.id)
                        toast('Page deleted', 'success')
                        onReload()
                      }}><Trash2Icon /></button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddPageModal client={client} profiles={profiles} hasSeo={hasSeo}
        onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); onReload() }} />}
      {showBulkAdd && <BulkAddPagesModal client={client} profiles={profiles}
        onClose={() => setShowBulkAdd(false)} onSaved={() => { setShowBulkAdd(false); onReload() }} />}
      {editingPage && <PageBuildSidePanel
        client={client} profiles={profiles} hasSeo={hasSeo} currentUser={currentUser}
        page={pages.find(p => p.id === editingPage.id) || editingPage}
        onClose={() => setEditingPage(null)}
        onUpdated={onReload} />}
    </div>
  )
}

// Small Trash2 icon — using inline SVG since we already import the lucide Plus etc
function Trash2Icon() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
}

// ────────────────────────────────────────────────────────────
// Add Page Modal — single
// ────────────────────────────────────────────────────────────
function AddPageModal({ client, profiles, hasSeo, onClose, onSaved }) {
  const toast = useToast()
  const [form, setForm] = useState({
    page_name: '', page_url: '', keyword_choice: '', topic: '',
    assigned_to: '', due_date: '', is_key_page: false,
    faq_questions: '', topics_to_include: '', structural_guidelines: '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.page_name.trim()) { toast('Enter a page name', 'error'); return }
    setSaving(true)
    const { error } = await supabase.from('seo_pages').insert({
      client_id: client.id,
      page_name: form.page_name,
      page_url: form.page_url || null,
      keyword_choice: form.keyword_choice || null,
      topic: form.topic || null,
      assigned_to: form.assigned_to || null,
      due_date: form.due_date || null,
      is_key_page: form.is_key_page,
      faq_questions: form.faq_questions || null,
      topics_to_include: form.topics_to_include || null,
      structural_guidelines: form.structural_guidelines || null,
      build_status: 'not_started',
    })
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast('Page added', 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">Add Page</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Page Name *</label>
            <input className="form-control" value={form.page_name} onChange={e => set('page_name', e.target.value)} placeholder="e.g. Home, About, Anxiety Therapy" />
          </div>
          <div className="form-group">
            <label className="form-label">Page URL (or slug)</label>
            <input className="form-control" value={form.page_url} onChange={e => set('page_url', e.target.value)} placeholder="/about or https://..." />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginBottom: 14 }}>
            <input type="checkbox" checked={form.is_key_page} onChange={e => set('is_key_page', e.target.checked)} />
            ⭐ This is a key page (AD builds it as a template)
          </label>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assignee</label>
              <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                <option value="">Unassigned</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-control" type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
            </div>
          </div>

          {hasSeo && (
            <>
              <div className="form-group">
                <label className="form-label">Target Keyword (SEO)</label>
                <input className="form-control" value={form.keyword_choice} onChange={e => set('keyword_choice', e.target.value)} placeholder="e.g. anxiety therapist near me" />
              </div>
              <div className="form-group">
                <label className="form-label">Topic</label>
                <input className="form-control" value={form.topic} onChange={e => set('topic', e.target.value)} placeholder="e.g. Anxiety" />
              </div>
              <details style={{ marginTop: 4 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12.5, color: 'var(--teal)', fontWeight: 600 }}>+ Content guidance (FAQ, topics, structure)</summary>
                <div style={{ marginTop: 10 }}>
                  <div className="form-group">
                    <label className="form-label">FAQ Questions</label>
                    <textarea className="form-control" rows={3} value={form.faq_questions} onChange={e => set('faq_questions', e.target.value)} placeholder="One question per line" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Topics to Include</label>
                    <textarea className="form-control" rows={3} value={form.topics_to_include} onChange={e => set('topics_to_include', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Structural Guidelines</label>
                    <textarea className="form-control" rows={3} value={form.structural_guidelines} onChange={e => set('structural_guidelines', e.target.value)} />
                  </div>
                </div>
              </details>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Adding...' : 'Add Page'}</button>
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Bulk Add Pages Modal — paste or multi-row
// ────────────────────────────────────────────────────────────
function BulkAddPagesModal({ client, profiles, onClose, onSaved }) {
  const toast = useToast()
  const blankRow = () => ({ page_name: '', keyword_choice: '', is_key_page: false })
  const [rows, setRows] = useState([blankRow(), blankRow(), blankRow(), blankRow(), blankRow()])
  const [pasteValue, setPasteValue] = useState('')
  const [defaultAssignee, setDefaultAssignee] = useState('')
  const [saving, setSaving] = useState(false)

  const updateRow = (idx, field, value) => setRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  const addMore = (n = 5) => setRows(rs => [...rs, ...Array(n).fill(0).map(blankRow)])
  const removeRow = (idx) => setRows(rs => rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs)

  const applyPaste = () => {
    const lines = pasteValue.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0)
    if (lines.length === 0) { toast('Paste some lines first', 'error'); return }
    const parsed = lines.map(line => {
      const parts = line.includes('\t') ? line.split('\t') : line.split(',').map(p => p.trim())
      return { ...blankRow(), page_name: parts[0] || '', keyword_choice: parts[1] || '' }
    })
    setRows(parsed)
    setPasteValue('')
    toast(`Loaded ${parsed.length} rows from paste`, 'success')
  }

  const save = async () => {
    const filled = rows.filter(r => r.page_name && r.page_name.trim())
    if (filled.length === 0) { toast('Add at least one page name', 'error'); return }
    setSaving(true)
    const payload = filled.map((r, idx) => ({
      client_id: client.id,
      page_name: r.page_name,
      keyword_choice: r.keyword_choice || null,
      is_key_page: r.is_key_page || false,
      assigned_to: defaultAssignee || null,
      build_status: 'not_started',
      sort_order: idx * 10,
    }))
    const { error } = await supabase.from('seo_pages').insert(payload)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast(`Added ${payload.length} pages!`, 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-header">
          <span className="modal-title">Bulk Add Pages</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', padding: 14, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📋 Paste from spreadsheet (optional)</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
              One page per line. Format: "Page Name, Keyword" (commas or tabs).
            </div>
            <textarea rows={4} value={pasteValue} onChange={e => setPasteValue(e.target.value)}
              placeholder="Home, online therapy&#10;About, trauma informed&#10;Anxiety Therapy, anxiety therapist"
              style={{ width: '100%', padding: 8, fontFamily: 'monospace', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6 }} />
            <button className="btn btn-ghost btn-sm" onClick={applyPaste} style={{ marginTop: 6 }}>Load into rows below</button>
          </div>

          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Default Assignee</label>
            <select className="form-control" value={defaultAssignee} onChange={e => setDefaultAssignee(e.target.value)}>
              <option value="">— Unassigned —</option>
              {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{rows.length} rows</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: 8, padding: 8 }}>
            {rows.map((r, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr auto 32px', gap: 6, alignItems: 'center' }}>
                <input value={r.page_name} onChange={e => updateRow(idx, 'page_name', e.target.value)} placeholder="Page name" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />
                <input value={r.keyword_choice} onChange={e => updateRow(idx, 'keyword_choice', e.target.value)} placeholder="Keyword (optional)" className="form-control" style={{ fontSize: 12, padding: '4px 8px' }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={r.is_key_page} onChange={e => updateRow(idx, 'is_key_page', e.target.checked)} />
                  ⭐ key
                </label>
                <button className="btn-icon" onClick={() => removeRow(idx)} title="Remove row"><X size={12} /></button>
              </div>
            ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => addMore(5)} style={{ marginTop: 8 }}><Plus size={12} /> Add 5 more rows</button>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : `🚀 Add ${rows.filter(r => r.page_name && r.page_name.trim()).length} Pages`}</button>
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Page Build Side Panel
// ────────────────────────────────────────────────────────────
function PageBuildSidePanel({ client, profiles, hasSeo, page, onClose, onUpdated, currentUser }) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const assignee = profiles.find(p => p.id === page.assigned_to)

  const update = async (updates) => {
    setSaving(true)
    const { error } = await supabase.from('seo_pages').update(updates).eq('id', page.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    onUpdated()
  }

  const hasAd = !!client?.account_director_id

  const sendToClientEmail = () => {
    const clientName = client?.name || 'your practice'
    const myName = profiles?.find(p => p.id === currentUser?.id)?.full_name || 'LMR Team'

    // Include every URL we have, labeled clearly
    const urlLines = []
    if (page.preview_url) urlLines.push(`Preview: ${page.preview_url}`)
    if (page.final_url) urlLines.push(`Live URL: ${page.final_url}`)
    if (page.page_url && page.page_url !== page.preview_url && page.page_url !== page.final_url) {
      urlLines.push(`Page: ${page.page_url}`)
    }

    const subject = encodeURIComponent(`Page ready for your review: ${page.page_name}`)
    const body = encodeURIComponent(
      `Hi,\n\nWe have a page draft ready for your review.\n\n` +
      `Page: ${page.page_name}\n` +
      (urlLines.length ? urlLines.join('\n') + '\n' : '') +
      `\nPlease let us know:\n• ✅ Approved as-is\n• ✏️ Revisions needed\n\n` +
      `Best,\n${myName}`
    )
    const clientEmail = client?.contact_email || ''
    window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(clientEmail)}&su=${subject}&body=${body}`, '_blank')
  }

  const sendToAd = async () => {
    if (!client?.account_director_id) return
    await update({ awaiting_ad_approval: true })
    if (client.account_director_id !== currentUser?.id) {
      await supabase.from('notifications').insert({
        user_id: client.account_director_id,
        type: 'comment',
        message: `Page needs your approval: "${page.page_name}" (${client.name})`,
      })
    }
    toast('Sent to AD for approval', 'success')
  }

  // Build step status — 3 states for review steps, 2 for plain steps
  const pageCreatedDone = !!page.content_written
  const adApproved = !!page.internal_review_done
  const adAwaiting = !!page.awaiting_ad_approval && !adApproved
  const clientApproved = !!page.client_review_done
  const clientAwaiting = !!page.awaiting_client_approval && !clientApproved

  const stepsDone = [pageCreatedDone, adApproved, clientApproved].filter(Boolean).length
  const totalSteps = 3
  const allStepsDone = stepsDone === totalSteps

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 580, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{ background: 'var(--navy)', padding: '18px 22px', color: '#fff', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Page · {client.name}
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginTop: 4 }}>
                {page.is_key_page && <span style={{ marginRight: 6 }}>⭐</span>}
                {page.page_name}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                {assignee?.full_name || 'Unassigned'}
                {page.due_date && ` · Due ${safeFmt(page.due_date, 'MMM d')}`}
              </div>
            </div>
            <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={onClose}><X size={16} /></button>
          </div>
          {page.build_status === 'live' && (
            <div style={{ marginTop: 10, fontSize: 12, background: 'rgba(255,255,255,0.15)', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12 }}>
              ✓ Live
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', flex: 1, overflowY: 'auto' }}>
          {/* Basics */}
          <div className="form-group">
            <label className="form-label">Page Name</label>
            <input className="form-control" defaultValue={page.page_name || ''} onBlur={e => e.target.value !== (page.page_name || '') && update({ page_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Page URL / Slug</label>
            <input className="form-control" defaultValue={page.page_url || ''} onBlur={e => e.target.value !== (page.page_url || '') && update({ page_url: e.target.value || null })} placeholder="/about or https://..." />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginBottom: 14 }}>
            <input type="checkbox" checked={page.is_key_page || false} onChange={e => update({ is_key_page: e.target.checked })} />
            ⭐ Key page (AD builds it as a template)
          </label>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assignee</label>
              <select className="form-control" value={page.assigned_to || ''} onChange={e => update({ assigned_to: e.target.value || null })}>
                <option value="">Unassigned</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-control" type="date" value={page.due_date || ''} onChange={e => update({ due_date: e.target.value || null })} />
            </div>
          </div>

          {/* Build workflow steps */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 20, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Build Steps</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: allStepsDone ? '#2e6e3a' : 'var(--text-muted)' }}>
              {stepsDone} / {totalSteps} done
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Step 1: Page created (simple toggle) */}
            <WebsiteWorkflowStep
              done={pageCreatedDone}
              label="Page created"
              actionLabel="Mark page created"
              onClick={() => update({ content_written: !pageCreatedDone })}
            />

            {/* Step 2: AD approval (3-state) */}
            <PageApprovalStep
              kind="ad"
              hasAd={hasAd}
              approved={adApproved}
              awaiting={adAwaiting}
              onSend={sendToAd}
              onMarkApproved={() => update({ internal_review_done: true, awaiting_ad_approval: false })}
              onUndo={() => update({ internal_review_done: false, awaiting_ad_approval: false })}
            />

            {/* Step 3: Client approval (3-state with email action) */}
            <PageApprovalStep
              kind="client"
              hasAd={true}   // client always exists if we're in this workspace
              approved={clientApproved}
              awaiting={clientAwaiting}
              onSend={async () => {
                await update({ awaiting_client_approval: true })
                sendToClientEmail()
              }}
              onMarkApproved={() => update({ client_review_done: true, awaiting_client_approval: false })}
              onUndo={() => update({ client_review_done: false, awaiting_client_approval: false })}
            />
          </div>

          {/* Mark live button */}
          <div style={{ marginTop: 18 }}>
            {page.build_status !== 'live' ? (
              <button className="btn btn-gold" style={{ width: '100%' }} disabled={!allStepsDone || saving}
                onClick={() => update({ build_status: 'live' })}>
                ✅ Mark this page live
              </button>
            ) : (
              <button className="btn btn-ghost" style={{ width: '100%' }} onClick={() => update({ build_status: 'in_progress' })}>
                ↺ Reopen — un-mark as live
              </button>
            )}
            {!allStepsDone && page.build_status !== 'live' && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, textAlign: 'center' }}>
                Complete all {totalSteps} build steps above first.
              </div>
            )}
          </div>

          {/* URLs */}
          <div className="form-group" style={{ marginTop: 20 }}>
            <label className="form-label">Preview URL (pre-launch)</label>
            <input className="form-control" defaultValue={page.preview_url || ''} placeholder="https://..." onBlur={e => e.target.value !== (page.preview_url || '') && update({ preview_url: e.target.value || null })} />
          </div>
          <div className="form-group">
            <label className="form-label">Final URL (live)</label>
            <input className="form-control" defaultValue={page.final_url || ''} placeholder="https://..." onBlur={e => e.target.value !== (page.final_url || '') && update({ final_url: e.target.value || null })} />
          </div>

          {/* SEO section — only if hasSeo */}
          {hasSeo && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 22, marginBottom: 10 }}>SEO Content Guidance</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 10 }}>
                When filled out here, this page automatically appears in the SEO Workspace ready for Yoast optimization.
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Target Keyword</label>
                  <input className="form-control" defaultValue={page.keyword_choice || ''} onBlur={e => e.target.value !== (page.keyword_choice || '') && update({ keyword_choice: e.target.value || null })} placeholder="e.g. anxiety therapist near me" />
                </div>
                <div className="form-group">
                  <label className="form-label">Topic</label>
                  <input className="form-control" defaultValue={page.topic || ''} onBlur={e => e.target.value !== (page.topic || '') && update({ topic: e.target.value || null })} placeholder="e.g. Anxiety" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">FAQ Questions</label>
                <textarea className="form-control" rows={4} defaultValue={page.faq_questions || ''} onBlur={e => e.target.value !== (page.faq_questions || '') && update({ faq_questions: e.target.value || null })} placeholder="What is X?&#10;How does Y work?" />
              </div>
              <div className="form-group">
                <label className="form-label">Topics to Include</label>
                <textarea className="form-control" rows={4} defaultValue={page.topics_to_include || ''} onBlur={e => e.target.value !== (page.topics_to_include || '') && update({ topics_to_include: e.target.value || null })} />
              </div>
              <div className="form-group">
                <label className="form-label">Structural Guidelines</label>
                <textarea className="form-control" rows={4} defaultValue={page.structural_guidelines || ''} onBlur={e => e.target.value !== (page.structural_guidelines || '') && update({ structural_guidelines: e.target.value || null })} />
              </div>
            </>
          )}

          {/* Notes */}
          <div className="form-group" style={{ marginTop: 20 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={3} defaultValue={page.notes || ''} onBlur={e => e.target.value !== (page.notes || '') && update({ notes: e.target.value || null })} />
          </div>
        </div>
      </div>
    </div>
  )
}

// 3-state approval step (used for both AD and client review steps)
function PageApprovalStep({ kind, hasAd, approved, awaiting, onSend, onMarkApproved, onUndo }) {
  const label = kind === 'ad' ? 'Internal review (AD)' : 'Client review'
  const sendLabel = kind === 'ad' ? '📨 Send to AD' : '📨 Send to client'
  const approvedLabel = kind === 'ad' ? '✓ AD approved' : '✓ Client approved'
  const awaitingLabel = kind === 'ad' ? '⏳ Awaiting AD approval' : '⏳ Sent to client — awaiting'

  const bgColor = approved ? '#e8f5eb' : awaiting ? '#fff8e1' : 'var(--cream)'
  const borderColor = approved ? '#c2dec8' : awaiting ? '#f0d98a' : 'var(--border-light)'
  const labelColor = approved ? '#2e6e3a' : awaiting ? '#8a6000' : 'var(--text-primary)'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 8 }}>
      <div style={{
        width: 22, height: 22, borderRadius: 6,
        background: approved ? '#2e6e3a' : '#fff', border: `1.5px solid ${approved ? '#2e6e3a' : 'var(--border)'}`,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
        flexShrink: 0,
      }}>{approved ? '✓' : awaiting ? '⏳' : ''}</div>
      <div style={{ flex: 1, fontSize: 13, fontWeight: approved ? 500 : 600, color: labelColor }}>
        {approved ? approvedLabel : awaiting ? awaitingLabel : label}
        {kind === 'ad' && !hasAd && !approved && !awaiting && (
          <div style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--text-muted)', marginTop: 2 }}>
            No AD set for this client — assign one in the client profile to enable "Send to AD."
          </div>
        )}
      </div>
      {approved ? (
        <button onClick={onUndo} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', padding: '2px 6px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
          Undo
        </button>
      ) : awaiting ? (
        <>
          <button onClick={onMarkApproved} style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
            Mark approved
          </button>
          <button onClick={onUndo} style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', padding: '2px 6px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
            Cancel
          </button>
        </>
      ) : (
        <>
          {(kind === 'client' || hasAd) && (
            <button onClick={onSend} style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
              {sendLabel}
            </button>
          )}
          <button onClick={onMarkApproved} style={{ background: 'var(--cream-dark)', color: 'var(--text-primary)', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
            Mark approved
          </button>
        </>
      )}
    </div>
  )
}

// Workflow step component (matches the one in SEOWorkspace — same UX)
function WebsiteWorkflowStep({ done, label, actionLabel, onClick }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
      background: done ? '#e8f5eb' : 'var(--cream)',
      border: `1px solid ${done ? '#c2dec8' : 'var(--border-light)'}`,
      borderRadius: 8, cursor: 'pointer',
    }}>
      <div style={{
        width: 22, height: 22, borderRadius: 6,
        background: done ? '#2e6e3a' : '#fff', border: `1.5px solid ${done ? '#2e6e3a' : 'var(--border)'}`,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
        flexShrink: 0,
      }}>{done ? '✓' : ''}</div>
      <div style={{ flex: 1, fontSize: 13, fontWeight: done ? 500 : 600, color: done ? '#2e6e3a' : 'var(--text-primary)' }}>
        {done ? `✓ ${label}` : label}
      </div>
      {!done && (
        <button onClick={e => { e.stopPropagation(); onClick() }}
          style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
          {actionLabel}
        </button>
      )}
      {done && (
        <button onClick={e => { e.stopPropagation(); onClick() }}
          style={{ background: 'transparent', color: 'var(--text-muted)', border: 'none', padding: '2px 6px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
          Undo
        </button>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// SITTING 2: Aesthetics tab
// ════════════════════════════════════════════════════════════
function AestheticsTab({ client, profiles, onOpenTask }) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Pull aesthetic + brand/logo tasks for this client
    const ids = ['std-create-mood-boards', 'std-build-aesthetic-1', 'std-build-aesthetic-2', 'std-build-aesthetic-3', 'std-logo-concepts', 'std-brand-kit-presentation', 'std-build-aesthetic-key-pages']
    supabase.from('tasks').select('*')
      .eq('client_id', client.id)
      .in('template_id', ids)
      .then(({ data }) => { setTasks(data || []); setLoading(false) })
  }, [client.id])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>

  const moodBoards = tasks.filter(t => t.template_id === 'std-create-mood-boards')
  const aesthetics = tasks.filter(t => t.template_id?.startsWith('std-build-aesthetic-') && t.template_id !== 'std-build-aesthetic-key-pages')
  const branding = tasks.filter(t => ['std-logo-concepts', 'std-brand-kit-presentation'].includes(t.template_id))
  const keyPages = tasks.filter(t => t.template_id === 'std-build-aesthetic-key-pages')

  if (tasks.length === 0) {
    return (
      <div className="empty-state">
        <div style={{ fontSize: 48, marginBottom: 12 }}>🎨</div>
        <h3>No aesthetics tasks yet</h3>
        <p>Aesthetics are typically added during the kickoff. If this client needs aesthetic options, run the Kickoff Builder and pick "3 options" for aesthetics.</p>
      </div>
    )
  }

  return (
    <div>
      {moodBoards.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Mood Boards</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {moodBoards.map(t => <SimpleTaskCard key={t.id} task={t} profiles={profiles} onOpenTask={onOpenTask} />)}
          </div>
        </>
      )}

      {aesthetics.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Aesthetic Options</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 24 }}>
            {aesthetics.map(t => <AestheticCard key={t.id} task={t} profiles={profiles} onOpenTask={onOpenTask} />)}
          </div>
        </>
      )}

      {branding.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Branding</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {branding.map(t => <SimpleTaskCard key={t.id} task={t} profiles={profiles} onOpenTask={onOpenTask} />)}
          </div>
        </>
      )}

      {keyPages.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Key Pages Build (from approved aesthetic)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {keyPages.map(t => <SimpleTaskCard key={t.id} task={t} profiles={profiles} onOpenTask={onOpenTask} />)}
          </div>
        </>
      )}
    </div>
  )
}

function AestheticCard({ task, profiles, onOpenTask }) {
  const assignee = profiles.find(p => p.id === task.assigned_to)
  const isApproved = ['approved', 'client_approved', 'live'].includes(task.status)
  const docLink = (task.deliverable_links || task.google_doc_links || [])[0]
  return (
    <div className="card" style={{ cursor: onOpenTask ? 'pointer' : 'default', borderLeft: `3px solid ${isApproved ? '#2e6e3a' : 'var(--border-light)'}` }}
      onClick={() => onOpenTask && onOpenTask(task)}>
      <div className="card-body" style={{ padding: '14px 16px' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
          {isApproved && '✅ '}{task.title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
          {assignee?.full_name || 'Unassigned'} · Status: <strong>{task.status?.replace(/_/g, ' ')}</strong>
        </div>
        {docLink && (
          <a href={docLink} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: 'var(--teal)' }}>
            ↗ View deliverable
          </a>
        )}
        {onOpenTask && (
          <div style={{ fontSize: 11, color: 'var(--teal)', marginTop: 8, fontWeight: 600 }}>Open task →</div>
        )}
      </div>
    </div>
  )
}

function SimpleTaskCard({ task, profiles, onOpenTask }) {
  const assignee = profiles.find(p => p.id === task.assigned_to)
  const isDone = ['approved', 'client_approved', 'live'].includes(task.status)
  return (
    <div className="card" style={{ cursor: onOpenTask ? 'pointer' : 'default', borderLeft: `3px solid ${isDone ? '#2e6e3a' : 'var(--border-light)'}` }}
      onClick={() => onOpenTask && onOpenTask(task)}>
      <div className="card-body" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{isDone && '✓ '}{task.title}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
            {assignee?.full_name || 'Unassigned'} · {task.status?.replace(/_/g, ' ')}
          </div>
        </div>
        {onOpenTask && <span style={{ fontSize: 11.5, color: 'var(--teal)', fontWeight: 600 }}>Open →</span>}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// SITTING 2: Audit tab
// ════════════════════════════════════════════════════════════
function AuditTab({ client, profiles, onOpenTask }) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ids = ['std-audit-website-ux', 'std-audit-website-branding', 'std-audit-website-structure', 'std-compile-website-changes']
    supabase.from('tasks').select('*')
      .eq('client_id', client.id)
      .in('template_id', ids)
      .then(({ data }) => { setTasks(data || []); setLoading(false) })
  }, [client.id])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>

  const auditTasks = tasks.filter(t => t.template_id?.startsWith('std-audit-website-'))
  const compileTask = tasks.find(t => t.template_id === 'std-compile-website-changes')

  if (tasks.length === 0) {
    return (
      <div className="empty-state">
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
        <h3>No audit tasks yet</h3>
        <p>Audits are typically added during the kickoff if the client has an existing website. Run the Kickoff Builder and pick which audits to perform.</p>
      </div>
    )
  }

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Website Audits</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 20 }}>
        {auditTasks.map(t => <AestheticCard key={t.id} task={t} profiles={profiles} onOpenTask={onOpenTask} />)}
      </div>

      {compileTask && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Compile Recommendations for Client</div>
          <SimpleTaskCard task={compileTask} profiles={profiles} onOpenTask={onOpenTask} />
        </>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// SITTING 2: Maintenance tab
// ════════════════════════════════════════════════════════════
function MaintenanceTab({ client, maintenanceTask, profiles, onOpenTask, onReload }) {
  const toast = useToast()
  const [history, setHistory] = useState([])
  const [spawning, setSpawning] = useState(false)

  useEffect(() => {
    supabase.from('tasks').select('*')
      .eq('client_id', client.id)
      .eq('template_id', 'std-website-monthly-maintenance')
      .order('date_due', { ascending: false })
      .then(({ data }) => setHistory(data || []))
  }, [client.id, maintenanceTask?.id])

  const spawnNext = async () => {
    setSpawning(true)
    try {
      const { data: tpl } = await supabase.from('task_templates').select('*').eq('id', 'std-website-monthly-maintenance').maybeSingle()
      if (!tpl) { toast('Maintenance template not found', 'error'); return }
      // Schedule for the 1st of next month
      const due = new Date()
      due.setMonth(due.getMonth() + 1)
      due.setDate(1)
      const { error } = await supabase.from('tasks').insert({
        title: tpl.title,
        description: tpl.description,
        client_id: client.id,
        template_id: tpl.id,
        assigned_to: maintenanceTask?.assigned_to || null,
        status: 'not_started',
        service_area: 'website',
        approval_path: 'none',
        date_due: due.toISOString().slice(0, 10),
        checklist_items: (tpl.checklist_items || []).map(it => ({ ...it, checked: false })),
        is_recurring: true,
        recurrence_type: 'monthly',
        recurrence_day_of_month: 1,
        triggered_by: { source: 'maintenance_tab_manual_spawn' },
      })
      if (error) { toast('Error: ' + error.message, 'error'); return }
      toast('Next month maintenance task created', 'success')
      onReload()
    } finally {
      setSpawning(false)
    }
  }

  return (
    <div>
      <MaintenanceSummaryCard maintenanceTask={maintenanceTask} profiles={profiles} onOpenTask={onOpenTask} />

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="card-title">What runs each month</span>
        </div>
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <ul style={{ paddingLeft: 18, lineHeight: 1.8 }}>
            <li>Update WordPress core to the latest stable version</li>
            <li>Update all active plugins</li>
            <li>Update Divi theme</li>
            <li>Update PHP version (verify with host first if any compatibility concerns)</li>
            <li>Verify backups are running and recent (within the last week)</li>
            <li>Spot-check the site loads correctly on mobile and desktop after updates</li>
            <li>Note any issues and create follow-up tasks if needed</li>
          </ul>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Maintenance History ({history.length})</div>
        <button className="btn btn-ghost btn-sm" onClick={spawnNext} disabled={spawning}>
          {spawning ? 'Creating...' : '+ Create Next Month Task'}
        </button>
      </div>
      {history.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No maintenance tasks yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {history.map(t => {
            const items = t.checklist_items || []
            const done = items.filter(i => i.checked).length
            const isDone = done === items.length && items.length > 0
            const assignee = profiles.find(p => p.id === t.assigned_to)
            return (
              <div key={t.id} className="card" style={{ cursor: onOpenTask ? 'pointer' : 'default' }}
                onClick={() => onOpenTask && onOpenTask(t)}>
                <div className="card-body" style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {isDone ? '✓ ' : ''}{t.date_due ? safeFmt(t.date_due, 'MMM yyyy') : '(no date)'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {done}/{items.length} steps · {assignee?.full_name || 'Unassigned'}
                    </div>
                  </div>
                  {onOpenTask && <span style={{ fontSize: 11.5, color: 'var(--teal)', fontWeight: 600 }}>Open →</span>}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// SITTING 2: Reference tab
// ════════════════════════════════════════════════════════════
function ReferenceTab() {
  const toast = useToast()
  const diviApiKey = '0a185a6cc7c3c4ffb996fca87960b290f46ccf5c'
  const diviUsername = 'laur1912'

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast(`${label} copied to clipboard`, 'success'),
      () => toast('Failed to copy', 'error')
    )
  }

  return (
    <div>
      {/* Build Credentials — agency-wide */}
      <div className="card">
        <div className="card-header"><span className="card-title">💻 Build Credentials (agency-wide)</span></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr auto', gap: '10px 14px', alignItems: 'center', fontSize: 13 }}>
            <div style={{ color: 'var(--text-muted)' }}>Divi username</div>
            <code style={{ background: 'var(--cream)', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>{diviUsername}</code>
            <button className="btn btn-ghost btn-sm" onClick={() => copy(diviUsername, 'Username')}>Copy</button>

            <div style={{ color: 'var(--text-muted)' }}>Divi API key</div>
            <code style={{ background: 'var(--cream)', padding: '4px 8px', borderRadius: 4, fontSize: 11, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{diviApiKey}</code>
            <button className="btn btn-ghost btn-sm" onClick={() => copy(diviApiKey, 'API key')}>Copy</button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.5 }}>
            Use these when activating Divi theme & builder on a new client site. The <strong>per-client Divi password</strong> and other client-specific credentials live in the Site Reference card on the Overview tab.
          </div>
        </div>
      </div>
    </div>
  )
}